import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ErrorContextType {
  showError: (message: string, title?: string) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  hideError: () => void;
  hideSuccess: () => void;
  showValidationError: (errors: Record<string, string>) => void;
  showConfirmDialog: (title: string, message: string, onConfirm: () => void) => void;
  validateEmail: (email: string) => boolean;
  validateRequired: (value: any, fieldName: string) => string | null;
  validatePhone: (phone: string) => boolean;
  validateAadhar: (aadhar: string) => boolean;
  validatePincode: (pincode: string) => boolean;
  validatePassword: (password: string) => { isValid: boolean; errors: string[] };
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const showSnackbar = (message: string, severity: AlertColor) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const showError = (message: string, title?: string) => {
    showSnackbar(title ? `${title}: ${message}` : message, 'error');
  };

  const showSuccess = (message: string) => {
    showSnackbar(message, 'success');
  };

  const showWarning = (message: string) => {
    showSnackbar(message, 'warning');
  };

  const showInfo = (message: string) => {
    showSnackbar(message, 'info');
  };

  const hideError = () => {
    if (snackbar.severity === 'error') {
      setSnackbar(prev => ({ ...prev, open: false }));
    }
  };

  const hideSuccess = () => {
    if (snackbar.severity === 'success') {
      setSnackbar(prev => ({ ...prev, open: false }));
    }
  };

  const showValidationError = (errors: Record<string, string>) => {
    const errorMessages = Object.values(errors).filter(Boolean);
    if (errorMessages.length > 0) {
      showError(errorMessages.join('\n'), 'Validation Error');
    }
  };

  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm
    });
  };

  const handleConfirm = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleCancel = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateRequired = (value: any, fieldName: string): string | null => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${fieldName} is required`;
    }
    return null;
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateAadhar = (aadhar: string): boolean => {
    const aadharRegex = /^\d{4}\s\d{4}\s\d{4}$/;
    return aadharRegex.test(aadhar);
  };

  const validatePincode = (pincode: string): boolean => {
    const pincodeRegex = /^[1-9][0-9]{5}$/;
    return pincodeRegex.test(pincode);
  };

  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/(?=.*\d)/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push('Password must contain at least one special character (@$!%*?&)');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const value: ErrorContextType = {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    hideError,
    hideSuccess,
    showValidationError,
    showConfirmDialog,
    validateEmail,
    validateRequired,
    validatePhone,
    validateAadhar,
    validatePincode,
    validatePassword
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}

      {/* Global Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {confirmDialog.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}; 